import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Linkedin, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { submitContactForm, contactData } from '@/data/profile';
import AnimatedSection from './AnimatedSection';

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    objet: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.objet.trim()) newErrors.objet = "L'objet est requis";
    if (!formData.message.trim()) newErrors.message = 'Le message est requis';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      setSubmitSuccess(true);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        objet: '',
        message: ''
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
      setIsSubmitting(false);
    } catch (error) {
        setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 px-4 relative">
      {/* Gradient orbs */}
      <div className="absolute top-10 right-10 w-96 h-96 bg-gradient-to-br from-[#0066FF]/10 to-[#7B61FF]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-br from-[#FF4D8D]/10 to-[#00D4FF]/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-5xl mx-auto relative z-10">
        <AnimatedSection animation="fade-up">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">Contactez-moi</h2>
            <p className="text-xl text-[#a0a0a0]">
              Discutons de votre projet
            </p>
          </div>
        </AnimatedSection>
        
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <AnimatedSection animation="fade-right" delay={200}>
              <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#2a2a2a] hover:border-[#0066FF] transition-all duration-500 group cursor-pointer hover:-translate-y-1">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0066FF] to-[#7B61FF] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-lg">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#a0a0a0] mb-1">Email</p>
                    <a href={`mailto:${contactData.email}`} className="text-white hover:text-[#0066FF] transition-colors duration-300">
                      {contactData.email}
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fade-right" delay={300}>
              <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-[#2a2a2a] hover:border-[#7B61FF] transition-all duration-500 group cursor-pointer hover:-translate-y-1">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#7B61FF] to-[#FF4D8D] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-lg">
                    <Linkedin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#a0a0a0] mb-1">LinkedIn</p>
                    <a 
                      href={contactData.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white hover:text-[#7B61FF] transition-colors duration-300"
                    >
                      Pierre Iaccarino
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fade-right" delay={400}>
              <div className="bg-gradient-to-br from-[#0066FF]/10 to-[#7B61FF]/10 rounded-2xl p-6 border border-[#0066FF]/20 hover:border-[#0066FF]/40 transition-all duration-500 hover:-translate-y-1">
                <p className="text-[#e0e0e0] text-sm leading-relaxed">
                  <span className="font-semibold text-white">Disponible</span> pour des missions longue durée (plusieurs mois) en 100% remote.
                </p>
              </div>
            </AnimatedSection>
          </div>
          
          {/* Contact Form */}
          <AnimatedSection animation="fade-left" delay={300} className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6 bg-[#1a1a1a] rounded-2xl p-8 border border-[#2a2a2a] hover:border-[#2a2a2a]/80 transition-colors duration-300">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-white text-sm font-medium">Nom *</Label>
                  <Input
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="bg-[#0f0f0f] border-[#2a2a2a] text-white focus:border-[#0066FF] focus:ring-[#0066FF]/20 transition-all duration-300 h-11"
                    placeholder="Votre nom"
                  />
                  {errors.nom && <p className="text-sm text-[#FF4D8D]">{errors.nom}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="prenom" className="text-white text-sm font-medium">Prénom *</Label>
                  <Input
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    className="bg-[#0f0f0f] border-[#2a2a2a] text-white focus:border-[#7B61FF] focus:ring-[#7B61FF]/20 transition-all duration-300 h-11"
                    placeholder="Votre prénom"
                  />
                  {errors.prenom && <p className="text-sm text-[#FF4D8D]">{errors.prenom}</p>}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white text-sm font-medium">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-[#0f0f0f] border-[#2a2a2a] text-white focus:border-[#00D4FF] focus:ring-[#00D4FF]/20 transition-all duration-300 h-11"
                    placeholder="votre@email.com"
                  />
                  {errors.email && <p className="text-sm text-[#FF4D8D]">{errors.email}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="telephone" className="text-white text-sm font-medium">Téléphone</Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="bg-[#0f0f0f] border-[#2a2a2a] text-white focus:border-[#FF4D8D] focus:ring-[#FF4D8D]/20 transition-all duration-300 h-11"
                    placeholder="+33 6 00 00 00 00"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="objet" className="text-white text-sm font-medium">Objet *</Label>
                <Input
                  id="objet"
                  name="objet"
                  value={formData.objet}
                  onChange={handleChange}
                  className="bg-[#0f0f0f] border-[#2a2a2a] text-white focus:border-[#0066FF] focus:ring-[#0066FF]/20 transition-all duration-300 h-11"
                  placeholder="Objet de votre demande"
                />
                {errors.objet && <p className="text-sm text-[#FF4D8D]">{errors.objet}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message" className="text-white text-sm font-medium">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="bg-[#0f0f0f] border-[#2a2a2a] text-white focus:border-[#7B61FF] focus:ring-[#7B61FF]/20 transition-all duration-300 resize-none"
                  placeholder="Décrivez votre projet..."
                />
                {errors.message && <p className="text-sm text-[#FF4D8D]">{errors.message}</p>}
              </div>
              
              {submitSuccess && (
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#00D4FF]/10 to-[#0066FF]/10 border border-[#00D4FF]/30 rounded-xl animate-in fade-in duration-300">
                  <CheckCircle2 className="w-5 h-5 text-[#00D4FF]" />
                  <span className="text-white font-medium">Message envoyé avec succès!</span>
                </div>
              )}

              {submitError && (
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#FF4D8D]/10 to-[#7B61FF]/10 border border-[#FF4D8D]/30 rounded-xl animate-in fade-in duration-300">
                  <span className="text-[#FF4D8D] font-medium">Erreur : {submitError}</span>
                </div>
              )}
              
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#0066FF] to-[#7B61FF] hover:from-[#0052CC] hover:to-[#6347CC] text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-[#0066FF]/50 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Envoyer le message
                    <Send className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default Contact;